import React, { useEffect, useMemo, useState } from 'react';
import CentralCalls from '../../centralCalls';
import BarChartWidget from './widgets/BarChartWidget';
import LineChartWidget from './widgets/LineChartWidget';
import PieChartWidget from './widgets/PieChartWidget';
import DateFilterWidget from './widgets/DateFilterWidget';
import PhaseTypeFilterWidget from './widgets/PhaseTypeFilterWidget';
import SingleValueWidget from './widgets/SingleValueWidget';
import TableWidget from './widgets/TableWidget';
import TitleWidget from './widgets/TitleWidget';
import { getWidgetIconById } from './widgetIcons';

function parseJsonValue(value) {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function normalizeProcedureResult(raw) {
	if (Array.isArray(raw) && raw.length === 1 && raw[0] && typeof raw[0] === 'object' && 'json' in raw[0]) {
		return parseJsonValue(raw[0].json);
	}

	if (raw && typeof raw === 'object' && 'json' in raw) {
		return parseJsonValue(raw.json);
	}

	return raw;
}

function getHeaderLabel(widget) {
	return widget?.headerLabel || widget?.data?.headerLabel || '';
}

function getSubHeaderLabel(widget) {
	return widget?.subHeaderLabel || widget?.data?.subHeaderLabel || widget?.data?.subHeader || '';
}

function getNotes(widget) {
	const value = widget?.notes ?? widget?.data?.notes;
	return typeof value === 'string' ? value : '';
}

function getDefaultIconIdByType(normalizedType) {
	if (normalizedType === 'table') return 'table';
	if (normalizedType === 'bar' || normalizedType === 'barchart') return 'bar';
	if (normalizedType === 'line') return 'line';
	if (normalizedType === 'pie') return 'pie';
	if (normalizedType === 'single') return 'gauge';
	if (normalizedType === 'phasetypefilter') return 'filter';
	if (normalizedType === 'datefilter') return 'filter';
	if (normalizedType === 'titlewidget') return 'bar';
	return 'bar';
}

function normalizePayloadObject(raw) {
	if (!raw) {
		return {};
	}

	if (typeof raw === 'string') {
		const parsed = parseJsonValue(raw);
		return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
	}

	return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
}

function getFilterVariableName(widget, normalizedType) {
	const configured =
		widget?.filterVariableName ||
		widget?.filterParamKey ||
		widget?.data?.filterVariableName ||
		widget?.data?.filterParamKey;

	if (typeof configured === 'string' && configured.trim()) {
		return configured.trim();
	}

	if (normalizedType === 'phasetypefilter') {
		return 'phaseTypeNameId';
	}

	if (normalizedType === 'datefilter') {
		return 'date';
	}

	return widget?.id || normalizedType || 'filterValue';
}

function getDateDefaultOffset(widget, normalizedType) {
	if (normalizedType !== 'datefilter') {
		return null;
	}

	const configured = widget?.dateDefaultOffset ?? widget?.data?.dateDefaultOffset;
	if (configured === null || configured === undefined || configured === '') {
		return null;
	}

	if (typeof configured === 'number' && Number.isInteger(configured)) {
		return configured;
	}

	if (typeof configured === 'string') {
		const trimmed = configured.trim();
		if (!trimmed || !/^[-+]?\d+$/.test(trimmed)) {
			return null;
		}
		return Number.parseInt(trimmed, 10);
	}

	return null;
}

function renderWidgetByType(normalizedType, data, options) {
	if (normalizedType === 'table') return <TableWidget data={data} exportFileName={options?.exportFileName} />;
	if (normalizedType === 'bar' || normalizedType === 'barchart') {
		return <BarChartWidget data={data} />;
	}
	if (normalizedType === 'line') {
		return <LineChartWidget data={data} />;
	}
	if (normalizedType === 'pie') {
		return <PieChartWidget data={data} />;
	}
	if (normalizedType === 'single') {
		return <SingleValueWidget data={data} />;
	}
	if (normalizedType === 'titlewidget') {
		return (
			<TitleWidget
				headerLabel={options?.headerLabel}
				subHeaderLabel={options?.subHeaderLabel}
				backgroundColor={options?.backgroundColor}
			/>
		);
	}
	if (normalizedType === 'phasetypefilter') {
		return (
			<PhaseTypeFilterWidget
				value={options?.filterValue}
				onChange={options?.onFilterChange}
			/>
		);
	}
	if (normalizedType === 'datefilter') {
		return (
			<DateFilterWidget
				value={options?.filterValue}
				defaultOffsetDays={options?.defaultOffsetDays}
				onChange={options?.onFilterChange}
			/>
		);
	}

	return (
		<div className="flex h-full w-full items-center justify-center rounded-xl bg-slate-50 p-3 text-center text-xs text-slate-500">
			Unsupported widget type
		</div>
	);
}

function ReportWidget({ widget, filterValues = {}, onFilterValueChange, onWidgetPatch }) {
	const [data, setData] = useState(undefined);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(null);
	const [tableSearchText, setTableSearchText] = useState('');
	const [tableCanDownload, setTableCanDownload] = useState(false);
	const [requestDownloadToken, setRequestDownloadToken] = useState(0);
	const [isNotesPopupOpen, setIsNotesPopupOpen] = useState(false);
	const [notesDraft, setNotesDraft] = useState('');
	const [isSavingNotes, setIsSavingNotes] = useState(false);
	const [notesError, setNotesError] = useState('');

	const normalizedType = useMemo(() => (widget?.type || '').trim().toLowerCase(), [widget?.type]);
	const isFilterWidget = normalizedType.endsWith('filter');
	const procedureName = widget?.procedureName;
	const baseProcedurePayload = useMemo(
		() => normalizePayloadObject(widget?.parameters || widget?.parameterJson || widget?.procedureParams || widget?.procedurePayload),
		[widget?.parameters, widget?.parameterJson, widget?.procedureParams, widget?.procedurePayload]
	);
	const mergedProcedurePayload = useMemo(
		() => ({ ...baseProcedurePayload, ...(filterValues && typeof filterValues === 'object' ? filterValues : {}) }),
		[baseProcedurePayload, filterValues]
	);

	const headerLabel = getHeaderLabel(widget);
	const subHeaderLabel = getSubHeaderLabel(widget);
	const notes = getNotes(widget);
	const filterVariableName = getFilterVariableName(widget, normalizedType);
	const dateDefaultOffset = getDateDefaultOffset(widget, normalizedType);
	const filterValue = filterValues?.[filterVariableName] ?? '';
	const defaultIconId = getDefaultIconIdByType(normalizedType);
	const selectedIcon = getWidgetIconById(widget?.icon || defaultIconId);
	const HeaderIcon = selectedIcon?.Icon;
	const isTableWidget = normalizedType === 'table';
	const isTitleWidget = normalizedType === 'titlewidget';
	const showOuterHeader = !isTitleWidget && (headerLabel || subHeaderLabel || HeaderIcon);

	useEffect(() => {
		if (!isNotesPopupOpen) {
			setNotesDraft(notes);
			setNotesError('');
		}
	}, [notes, isNotesPopupOpen]);

	const handleSaveNotes = async () => {
		if (typeof onWidgetPatch !== 'function') {
			setIsNotesPopupOpen(false);
			return;
		}

		setIsSavingNotes(true);
		setNotesError('');
		try {
			await onWidgetPatch({
				notes: notesDraft,
				data: {
					notes: notesDraft,
				},
			});
			setIsNotesPopupOpen(false);
		} catch (error) {
			setNotesError('Failed to save notes.');
			console.error('Failed to save widget notes', error);
		} finally {
			setIsSavingNotes(false);
		}
	};

	useEffect(() => {
		let active = true;

		if (isFilterWidget) {
			setData(undefined);
			setLoadError(null);
			setLoading(false);
			return () => {
				active = false;
			};
		}

		if (!procedureName) {
			setData(undefined);
			setLoadError(null);
			setLoading(false);
			return () => {
				active = false;
			};
		}

		const load = async () => {
			setLoading(true);
			setLoadError(null);

			try {
				const raw = await CentralCalls.callStoredProcedureWithParams(procedureName, mergedProcedurePayload);
				if (!active) return;
				const normalized = normalizeProcedureResult(raw);
				const wrappedData = normalized && typeof normalized === 'object' && !Array.isArray(normalized) ? normalized.data : undefined;
				setData(wrappedData ?? normalized);
			} catch (error) {
				if (!active) return;
				setData(undefined);
				setLoadError(error instanceof Error ? error.message : 'Failed to load widget data');
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		};

		void load();

		return () => {
			active = false;
		};
	}, [procedureName, mergedProcedurePayload, isFilterWidget]);

	const renderContent = () => {
		if (loading) {
			return <div className="flex h-full w-full items-center justify-center text-xs text-slate-500">Loading...</div>;
		}

		if (loadError) {
			return <div className="flex h-full w-full items-center justify-center text-center text-xs text-red-700">{loadError}</div>;
		}

		if (isTableWidget) {
			return (
				<TableWidget
					data={data}
					columnOrder={widget?.columnOrder || widget?.data?.columnOrder || []}
					searchText={tableSearchText}
					exportFileName={headerLabel || subHeaderLabel || widget?.id || 'report-table-export'}
					onTableMetaChange={({ canDownload }) => {
						setTableCanDownload(Boolean(canDownload));
					}}
					requestDownloadToken={requestDownloadToken}
				/>
			);
		}

		return renderWidgetByType(normalizedType, data, {
			exportFileName: headerLabel || subHeaderLabel || widget?.id || 'report-table-export',
			headerLabel,
			subHeaderLabel,
			backgroundColor: widget?.backgroundColor,
			filterValue,
			defaultOffsetDays: dateDefaultOffset,
			onFilterChange: (value) => {
				if (typeof onFilterValueChange === 'function') {
					onFilterValueChange(filterVariableName, value);
				}
			},
		});
	};

	return (
		<div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
			{showOuterHeader ? (
				<div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-3 py-1.5">
					<div className="flex items-center justify-between gap-2">
						<div className="flex min-w-0 items-center gap-2">
							{HeaderIcon ? (
								<span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-700">
									<HeaderIcon className="h-4.5 w-4.5" />
								</span>
							) : null}
							<div className="min-w-0 flex flex-col">
								{headerLabel ? <h4 className="truncate text-sm font-bold leading-tight tracking-tight text-slate-900">{headerLabel}</h4> : null}
								{subHeaderLabel ? <span className="mt-0.5 text-center text-[10px] font-medium leading-snug text-slate-500 whitespace-normal break-words">{subHeaderLabel}</span> : null}
							</div>
						</div>

						<div className="ml-2 flex shrink-0 items-center gap-1.5">
							<button
								type="button"
								onClick={() => {
									setNotesDraft(notes);
									setNotesError('');
									setIsNotesPopupOpen(true);
								}}
								title="Notes"
								className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white/95 text-slate-700 shadow-sm transition hover:bg-slate-100"
							>
								<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
									<path d="M7 4.5H17C18.1 4.5 19 5.4 19 6.5V17.5C19 18.6 18.1 19.5 17 19.5H7C5.9 19.5 5 18.6 5 17.5V6.5C5 5.4 5.9 4.5 7 4.5Z" stroke="currentColor" strokeWidth="1.7" />
									<path d="M8.5 9H15.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
									<path d="M8.5 12H15.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
									<path d="M8.5 15H12.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
								</svg>
							</button>

							{isTableWidget && !widget?.hideSearchbar ? (
								<>
									<input
										type="text"
										value={tableSearchText}
										onChange={(event) => setTableSearchText(event.target.value)}
										placeholder="Search all..."
										className="h-7 w-[100px] rounded-full border border-slate-300/90 bg-white/95 px-3 text-xs text-slate-700 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
									/>
									<button
										type="button"
										onClick={() => setRequestDownloadToken((value) => value + 1)}
										title="Download CSV"
										className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white/95 text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
										disabled={!tableCanDownload}
									>
										<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
											<path d="M12 4v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
											<path d="M8.5 10.5L12 14l3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
											<path d="M5 18.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
										</svg>
									</button>
								</>
							) : null}
						</div>
					</div>
				</div>
			) : null}

			<div className="min-h-0 flex-1 p-2">
				{renderContent()}
			</div>

			{isNotesPopupOpen ? (
				<div className="fixed inset-0 z-[130000] flex items-center justify-center px-3">
					<div className="absolute inset-0 bg-black/35" onClick={() => !isSavingNotes && setIsNotesPopupOpen(false)} />
					<div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
							<h4 className="text-sm font-semibold text-slate-900">Widget Notes</h4>
							<button
								type="button"
								onClick={() => setIsNotesPopupOpen(false)}
								disabled={isSavingNotes}
								className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
								title="Close notes"
							>
								x
							</button>
						</div>
						<div className="px-3 py-3">
							<textarea
								value={notesDraft}
								onChange={(event) => setNotesDraft(event.target.value)}
								rows={7}
								placeholder="Add notes for this widget"
								className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
							/>
							{notesError ? <p className="mt-2 text-xs text-red-700">{notesError}</p> : null}
						</div>
						<div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2">
							<button
								type="button"
								onClick={() => setIsNotesPopupOpen(false)}
								disabled={isSavingNotes}
								className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSaveNotes}
								disabled={isSavingNotes}
								className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{isSavingNotes ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

export default ReportWidget;
